"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

const SCRIPT_ID = "ael-article-jsonld";
const PUBLISHED_DATE = "2026-02-17";

const ALLOWED_ROOT_SEGMENTS = new Set([
  "", // homepage
  "auto-bewerten",
  "auto-verkaufen",
  "kaputtes-auto-verkaufen",
  "fahrgestellnummer-pruefen",
  "ratgeber",
  "faq",
  "so-funktionierts",
  "ueber-uns",
  "kontaktiere-uns",
  "datenschutz",
  "impressum",
  "agb",
  "presse",
  "guides",
  "marken",
]);

const EXCLUDED_PREFIXES = [
  // Diese Route liefert ihr Article- und BreadcrumbList-JSON-LD bereits
  // serverseitig (src/app/fahrgestellnummer-pruefen/was-ist-die-fin/page.js).
  // Ohne diesen Eintrag haengt die Komponente hier ein ZWEITES Article-Objekt
  // an dieselbe URL — Playbook 2.3.
  "/fahrgestellnummer-pruefen/was-ist-die-fin",
  "/account",
  "/anmelden",
  "/logout",
  "/status",
  "/magic-link",
  "/email-praeferenzen",
  "/link-abgelaufen",
  "/preis",
];

function normalizeText(value) {
  return (value || "").replace(/\s+/g, " ").trim();
}

function isExcludedPath(pathname) {
  return EXCLUDED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function shouldInjectArticleSchema(pathname) {
  if (!pathname || isExcludedPath(pathname)) {
    return false;
  }

  const firstSegment = pathname.split("/").filter(Boolean)[0] || "";
  return ALLOWED_ROOT_SEGMENTS.has(firstSegment);
}

function getCanonicalUrl(pathname) {
  const canonical = document
    .querySelector('link[rel="canonical"]')
    ?.getAttribute("href");
  if (canonical) {
    return canonical;
  }
  return `https://www.autoeinfachlos.at${pathname}`;
}

function getPublishedDate() {
  return PUBLISHED_DATE;
}

function getOrganizationAuthor() {
  return {
    "@type": "Organization",
    name: "Autoeinfachlos",
    url: "https://www.autoeinfachlos.at",
  };
}

function getOrganizationPublisher() {
  return {
    "@type": "Organization",
    name: "Autoeinfachlos",
    url: "https://www.autoeinfachlos.at",
    logo: {
      "@type": "ImageObject",
      url: "https://www.autoeinfachlos.at/favicon.ico",
    },
  };
}

function buildArticleJsonLd(pathname) {
  const h1 = normalizeText(document.querySelector("h1")?.textContent);
  const titleFallback = normalizeText(
    document.title.replace(/\s*[|–-]\s*Autoeinfachlos.*$/i, "")
  );
  const description = normalizeText(
    document.querySelector('meta[name="description"]')?.getAttribute("content")
  );

  const headline = h1 || titleFallback;
  if (!headline || !description) {
    return null;
  }

  const canonicalUrl = getCanonicalUrl(pathname);

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    datePublished: getPublishedDate(),
    url: canonicalUrl,
    author: getOrganizationAuthor(),
    publisher: getOrganizationPublisher(),
  };
}

function extractFaqEntries() {
  const entries = [];

  // Single FAQ detail page: one question + full answer text
  const detailQuestion = normalizeText(
    document.querySelector(".FAQ-subcategory-wrapper h1")?.textContent
  );
  const detailAnswer = normalizeText(
    document.querySelector(".FAQ-subcategory-text")?.textContent
  );

  if (detailQuestion && detailAnswer) {
    entries.push({ question: detailQuestion, answer: detailAnswer });
    return entries;
  }

  // FAQ category overview pages: question cards with preview text
  const categoryQuestions = Array.from(
    document.querySelectorAll(".FAQ-c-right-question")
  );

  categoryQuestions.forEach((node) => {
    const question = normalizeText(node.querySelector("h2")?.textContent);
    const answer = normalizeText(node.querySelector("p")?.textContent);

    if (question && answer) {
      entries.push({ question, answer });
    }
  });

  return entries;
}

function buildFaqPageJsonLd(pathname) {
  const entries = extractFaqEntries();
  if (!entries.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    url: getCanonicalUrl(pathname),
    datePublished: getPublishedDate(),
    author: getOrganizationAuthor(),
    publisher: getOrganizationPublisher(),
    mainEntity: entries.map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: entry.answer,
      },
    })),
  };
}

function buildStructuredData(pathname) {
  if (pathname.startsWith("/faq")) {
    return buildFaqPageJsonLd(pathname);
  }
  return buildArticleJsonLd(pathname);
}

export default function AutoArticleStructuredData() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);

    if (!shouldInjectArticleSchema(pathname)) {
      if (existing) {
        existing.remove();
      }
      return;
    }

    const schema = buildStructuredData(pathname);
    if (!schema) {
      if (existing) {
        existing.remove();
      }
      return;
    }

    const script = existing || document.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);

    if (!existing) {
      document.head.appendChild(script);
    }
  }, [pathname]);

  return null;
}
